/**
 * Phase 9.6 — 💰 Creator Payouts Page
 * ------------------------------------
 * Surfaces available / pending / lifetime creator earnings, lets the
 * creator submit a payout request (≥ $25, manual review), and shows
 * their full payout history.
 */
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import StripeConnectCard from "@/components/StripeConnectCard";
import StripeCashOutPanel from "@/components/StripeCashOutPanel";

const METHODS = [
  { id: "paypal", label: "PayPal", icon: "💳", desc: "PayPal email address" },
  { id: "cashapp", label: "Cash App", icon: "💵", desc: "$cashtag" },
  { id: "zelle", label: "Zelle", icon: "🏦", desc: "Phone or email" },
  { id: "bank", label: "Bank Transfer", icon: "🏛️", desc: "Wire to bank account (intl. supported)" },
  { id: "usdt", label: "USDT Crypto Wallet", icon: "🪙", desc: "Stablecoin payout — best for international artists" },
  { id: "manual", label: "Manual / Other", icon: "✉️", desc: "Custom payout method" },
];

// Phase 9.7.17 — USDT supported networks. TRC20 is the primary path
// (lowest fees, widely used across Africa/Nigeria). Polygon + BEP20
// are backups. Volatile crypto (BTC/ETH) intentionally NOT offered.
const USDT_NETWORKS = [
  { id: "trc20", label: "TRC20 — Tron (recommended, low fees)" },
  { id: "polygon", label: "Polygon (MATIC)" },
  { id: "bep20", label: "BEP20 — Binance Smart Chain" },
];

const STATUS_BADGE = {
  pending: { label: "Pending", cls: "bg-amber-500/20 text-amber-200 border-amber-400/40" },
  approved: { label: "Approved", cls: "bg-blue-500/20 text-blue-200 border-blue-400/40" },
  denied: { label: "Denied", cls: "bg-rose-500/20 text-rose-200 border-rose-400/40" },
  paid: { label: "Paid", cls: "bg-emerald-500/20 text-emerald-200 border-emerald-400/40" },
};

const fmtUSD = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtCoins = (n) => Number(n || 0).toLocaleString();

const PayoutsPage = () => {
  const location = useLocation();
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [form, setForm] = useState({
    legal_name: "", artist_name: "", method: "paypal",
    handle: "", amount_usd: "", notes: "",
    // Phase 9.7.17 — method-specific extras
    wallet_address: "", network: "trc20", bank_details: "", country: "",
  });

  // Payout Funding Safety (Feb 2026) — accept an optional prefill from
  // the Earnings page's "Cash Out" CTA. We pass the verified cashable
  // amount through router state (never a URL query param — the amount
  // is not authoritative). Backend revalidates on submit via
  // `_verified_paid_available_coins`, so this is display-only.
  useEffect(() => {
    const s = location.state;
    if (s?.prefillFromCashout && typeof s.prefillAmountUsd === "number") {
      const amt = Math.max(25, Number(s.prefillAmountUsd || 0));
      setForm((f) => (f.amount_usd ? f : { ...f, amount_usd: amt.toFixed(2) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.prefillFromCashout]);

  const refresh = async () => {
    try {
      const [s, h] = await Promise.all([
        api.get("/payouts/me/summary"),
        api.get("/payouts/me/history"),
      ]);
      setSummary(s.data);
      setHistory(h.data?.items || []);
    } catch (e) {
      toast.error("Couldn't load payout data");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const amt = parseFloat(form.amount_usd);
    if (!form.legal_name.trim()) { toast.error("Legal name required"); return; }
    // Per-method required fields
    if (form.method === "usdt") {
      if (!form.wallet_address.trim() || form.wallet_address.trim().length < 20) {
        toast.error("Enter a valid USDT wallet address (≥ 20 chars)");
        return;
      }
      if (!form.network) {
        toast.error("Select a USDT network");
        return;
      }
    } else if (form.method === "bank") {
      if (!form.bank_details.trim() && !form.handle.trim()) {
        toast.error("Bank transfer requires bank details (account #, swift/IBAN, etc.)");
        return;
      }
    } else if (!form.handle.trim()) {
      toast.error("Payout handle/email required");
      return;
    }
    if (isNaN(amt) || amt < 25) { toast.error("Minimum payout is $25.00"); return; }
    setSubmitting(true);
    try {
      const payload = {
        legal_name: form.legal_name.trim(),
        artist_name: form.artist_name.trim(),
        method: form.method,
        handle: form.handle.trim(),
        amount_usd: amt,
        notes: form.notes.trim(),
        // Phase 9.7.17 — method-specific extras (sent for every request;
        // backend ignores them when not relevant)
        wallet_address: form.wallet_address.trim(),
        network: form.method === "usdt" ? form.network : "",
        bank_details: form.method === "bank" ? form.bank_details.trim() : "",
        country: form.country.trim(),
      };
      const r = await api.post("/payouts/me/request", payload);
      toast.success("Payout request submitted — admin will review.");
      setForm({
        legal_name: form.legal_name, artist_name: form.artist_name,
        method: form.method, handle: form.handle,
        amount_usd: "", notes: "",
        wallet_address: form.wallet_address, network: form.network,
        bank_details: form.bank_details, country: form.country,
      });
      setHistory((prev) => [r.data.request, ...prev]);
      await refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't submit request");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0514] text-white flex items-center justify-center">
        <p className="text-pink-200/60 text-sm">Loading payouts…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0514] via-[#0a0a14] to-black text-white">
      <div className="max-w-5xl mx-auto px-4 py-8" data-testid="payouts-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">🎁 Live / Gifts Payouts</h1>
            <p className="text-pink-200/80 text-sm mt-1">
              Cash out your <strong className="text-pink-100">verified paid-backed</strong> Live, Beam &amp; gift earnings.
              Two rails available below — Stripe Connect (automated) and Manual Request (admin-reviewed).
              {" "}
              <Link to="/producer" className="text-emerald-300 underline decoration-dotted">
                Selling beats? Visit Producer Dashboard →
              </Link>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/wallet" className="text-xs text-pink-300 hover:text-pink-200 underline">← Wallet</Link>
            <Link to="/payout-policy" data-testid="payouts-policy-link">
              <Button variant="outline" size="sm" className="text-pink-200 border-pink-500/40">
                Payout Policy
              </Button>
            </Link>
          </div>
        </div>

        {/* Cash-out progress banner — prominently shows either the
            $25 minimum runway or an eligible-to-cash-out CTA. Post
            Payout Funding Safety remediation these dollars come from
            spendable_available_usd (verified paid entries × $0.0075),
            NEVER from total_earned_coins × $0.01. */}
        {(() => {
          const cashable = Number(summary?.spendable_available_usd || 0);
          const min = Number(summary?.min_payout_usd || 25);
          const pct = Math.min(100, Math.floor((cashable / Math.max(min, 0.01)) * 100));
          const eligible = cashable >= min;
          return (
            <Card
              className={`p-5 mb-6 border ${eligible
                ? "bg-gradient-to-br from-emerald-500/15 to-teal-500/10 border-emerald-400/50"
                : "bg-gradient-to-br from-amber-500/10 to-orange-500/5 border-amber-400/40"}`}
              data-testid={eligible ? "payouts-eligible-banner" : "payouts-progress-banner"}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/70">
                    {eligible ? "✅ Eligible to Cash Out" : "🎯 Progress to Payout Minimum"}
                  </p>
                  <p className="text-3xl font-black text-white mt-2" data-testid="payouts-cashable-value">
                    {fmtUSD(cashable)} <span className="text-sm font-semibold text-pink-200/80">available to cash out</span>
                  </p>
                  {eligible ? (
                    <p className="text-xs text-emerald-100/90 mt-1" data-testid="payouts-eligible-msg">
                      You've reached the {fmtUSD(min)} payout minimum. Choose a rail below to request your payout.
                    </p>
                  ) : (
                    <p className="text-xs text-amber-100/90 mt-1" data-testid="payouts-progress-msg">
                      Earn <span className="font-bold text-amber-50">{fmtUSD(Math.max(0, min - cashable))}</span> more to reach the {fmtUSD(min)} payout minimum.
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-wider text-pink-200/70">Minimum</p>
                  <p className="text-xl font-black text-pink-100">{fmtUSD(min)}</p>
                </div>
              </div>
              <div
                className={`mt-3 h-2 rounded-full bg-black/40 overflow-hidden border ${eligible ? "border-emerald-400/40" : "border-amber-400/40"}`}
                data-testid="payouts-progress-bar"
              >
                <div
                  className={`h-full transition-all ${eligible ? "bg-gradient-to-r from-emerald-400 to-teal-400" : "bg-gradient-to-r from-amber-400 to-orange-400"}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </Card>
          );
        })()}

        {/* ──────────────────────────────────────────────────────────
            RAIL 1 — Stripe Connect (automated). Explicitly labeled.
            The StripeConnectCard renders account state + onboarding.
            ────────────────────────────────────────────────────────── */}
        <div className="mb-6" data-testid="payouts-rail-stripe">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.2em] font-black bg-blue-500/20 border border-blue-400/40 text-blue-100 px-2 py-1 rounded-full">
              Rail 1 · Automated
            </span>
            <span
              className="text-[10px] uppercase tracking-[0.2em] font-black bg-emerald-500 text-black px-2 py-1 rounded-full shadow"
              data-testid="payouts-rail-stripe-recommended"
            >
              ⭐ Recommended
            </span>
            <h2 className="text-lg font-black text-white">🏦 Stripe Connect / Bank Payout</h2>
          </div>
          <p className="text-[11px] text-blue-100/80 mb-3 leading-relaxed">
            Connect a Stripe Express account once. When you request a payout, RGM validates your eligible paid-backed earnings and initiates a transfer to your connected Stripe account. Stripe then pays out to your linked bank on your usual schedule. <strong className="text-blue-50">This is the only automated rail</strong> — Cash App / PayPal / Zelle / USDT / Bank transfer are all manual and reviewed by an admin.
          </p>
          <StripeConnectCard variant="live" onStatusChange={setStripeStatus} />
          <StripeCashOutPanel
            summary={summary}
            history={history}
            stripeStatus={stripeStatus}
            onSubmitted={async () => { await refresh(); }}
          />
        </div>

        {/* Balance grid — coin + verified-USD detail. */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-6">
          <Card className="p-4 bg-emerald-500/10 border-emerald-400/40" data-testid="payouts-available">
            <p className="text-[10px] uppercase tracking-wider text-emerald-200 font-bold">Available to Cash Out</p>
            <p className="text-3xl font-black text-emerald-100 mt-1">{fmtUSD(summary?.spendable_available_usd)}</p>
            <p className="text-[10px] text-emerald-200/70 mt-1">{fmtCoins(summary?.spendable_available_coins)} coins · verified paid-backed</p>
          </Card>
          <Card className="p-4 bg-amber-500/10 border-amber-400/40" data-testid="payouts-pending">
            <p className="text-[10px] uppercase tracking-wider text-amber-200 font-bold">Pending Clearance</p>
            <p className="text-3xl font-black text-amber-100 mt-1">{fmtUSD(summary?.pending_usd)}</p>
            <p className="text-[10px] text-amber-200/70 mt-1">{fmtCoins(summary?.pending_coins)} coins · {summary?.pending_hold_days}-day hold</p>
          </Card>
          <Card className="p-4 bg-slate-500/10 border-slate-400/40" data-testid="payouts-lifetime-paid">
            <p className="text-[10px] uppercase tracking-wider text-slate-200 font-bold">Lifetime Paid Out</p>
            <p className="text-3xl font-black text-slate-100 mt-1">
              {summary?.last_payout ? fmtUSD(summary.last_payout.amount_usd) : "$0.00"}
            </p>
            <p className="text-[10px] text-slate-200/70 mt-1">
              {summary?.last_payout ? `Last: ${summary.last_payout.method}` : "No completed payouts yet"}
            </p>
          </Card>
          <Card className="p-4 bg-purple-500/10 border-purple-400/40" data-testid="payouts-locked">
            <p className="text-[10px] uppercase tracking-wider text-purple-200 font-bold">Locked in Pending Requests</p>
            <p className="text-3xl font-black text-purple-100 mt-1">{fmtCoins(summary?.pending_payout_requested_coins || 0)}</p>
            <p className="text-[10px] text-purple-200/70 mt-1">coins committed to open requests</p>
          </Card>
        </div>

        {/* Rate reference (informational only). */}
        <Card className="p-3 mb-6 bg-black/30 border-pink-500/20 text-xs text-pink-200/70 flex flex-wrap gap-4">
          <span>💎 Min payout: <span className="font-bold text-pink-100">{fmtUSD(summary?.min_payout_usd)}</span></span>
          <span>⏱ Hold period: <span className="font-bold text-pink-100">{summary?.pending_hold_days} days</span></span>
          <span>💱 Rate: <span className="font-bold text-pink-100">1 earned coin = ${(summary?.payout_usd_per_earned_coin || 0).toFixed(4)}</span></span>
          {summary?.pending_payout_requested_coins > 0 && (
            <span>🔒 Locked in pending requests: <span className="font-bold text-pink-100">{fmtCoins(summary.pending_payout_requested_coins)} coins</span></span>
          )}
        </Card>

        {/* ──────────────────────────────────────────────────────────
            RAIL 2 — Manual Request (admin-reviewed, non-Stripe rails).
            ────────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-2 mb-2 flex-wrap" data-testid="payouts-rail-manual-header">
          <span className="text-[10px] uppercase tracking-[0.2em] font-black bg-pink-500/20 border border-pink-400/40 text-pink-100 px-2 py-1 rounded-full">
            Rail 2 · Manual
          </span>
          <span
            className="text-[10px] uppercase tracking-[0.2em] font-black bg-white/10 border border-white/30 text-white/80 px-2 py-1 rounded-full"
            data-testid="payouts-rail-manual-alternative"
          >
            Alternative
          </span>
          <h2 className="text-lg font-black text-white">✉️ Manual Payout Request</h2>
        </div>
        <p className="text-[11px] text-pink-100/70 mb-3 leading-relaxed">
          Use this when you prefer PayPal, Cash App, Zelle, Bank Transfer, USDT, or another destination. An admin reviews within 1-3 business days and processes the payment externally. <strong className="text-pink-50">These rails are NOT Stripe Connect</strong> — the destination address is provided by you and the transfer happens off-platform.
        </p>

        {/* Request form */}
        <Card className="p-5 sm:p-6 mb-6 bg-gradient-to-br from-pink-500/10 via-purple-500/5 to-emerald-500/10 border-pink-500/40" data-testid="payouts-rail-manual">
          <h2 className="text-xl font-black text-white mb-1">Submit a Manual Request</h2>
          <p className="text-xs text-pink-200/70 mb-4">
            Fill in your details — an admin reviews and pays out manually via your chosen rail.
          </p>
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="payouts-request-form">
            <div className="sm:col-span-1">
              <Label className="text-xs text-pink-200">Legal Name *</Label>
              <Input
                value={form.legal_name}
                onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
                placeholder="Full legal name"
                data-testid="payouts-input-legal-name"
                className="bg-black/40 border-pink-500/30 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-pink-200">Artist Name</Label>
              <Input
                value={form.artist_name}
                onChange={(e) => setForm({ ...form, artist_name: e.target.value })}
                placeholder="Stage name (optional)"
                data-testid="payouts-input-artist-name"
                className="bg-black/40 border-pink-500/30 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-pink-200">Method *</Label>
              <select
                value={form.method}
                onChange={(e) => setForm({ ...form, method: e.target.value })}
                data-testid="payouts-input-method"
                className="w-full bg-black/40 border border-pink-500/30 rounded-md px-3 py-2 text-white mt-1 text-sm"
              >
                {METHODS.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.icon} {m.label}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-pink-200/60 mt-1">
                {METHODS.find((m) => m.id === form.method)?.desc}
              </p>
            </div>
            <div>
              <Label className="text-xs text-pink-200">
                {form.method === "usdt"
                  ? "Identifier (optional)"
                  : form.method === "bank"
                  ? "Account Holder Name *"
                  : form.method === "zelle"
                  ? "Phone or Email *"
                  : "Handle / Email *"}
              </Label>
              <Input
                value={form.handle}
                onChange={(e) => setForm({ ...form, handle: e.target.value })}
                placeholder={
                  form.method === "usdt"
                    ? "Wallet nickname (e.g., my TRC20 wallet)"
                    : form.method === "bank"
                    ? "Name on the bank account"
                    : form.method === "zelle"
                    ? "Phone or email"
                    : form.method === "cashapp"
                    ? "$cashtag"
                    : "PayPal email"
                }
                data-testid="payouts-input-handle"
                className="bg-black/40 border-pink-500/30 text-white mt-1"
              />
            </div>

            {/* Phase 9.7.17 — USDT crypto wallet fields */}
            {form.method === "usdt" && (
              <>
                <div className="sm:col-span-2 p-3 rounded-md border border-amber-400/40 bg-amber-500/10">
                  <p className="text-[11px] font-bold text-amber-200 uppercase tracking-wider mb-1">
                    🪙 Stablecoin payout — verify carefully
                  </p>
                  <ul className="text-[11px] text-amber-100/90 space-y-1 list-disc list-inside">
                    <li>You are responsible for the wallet address — funds sent to a wrong network or address are <span className="font-bold">non-recoverable</span>.</li>
                    <li>Only USDT (Tether) — no other tokens supported.</li>
                    <li>TRC20 has the lowest fees and is recommended for African / Nigerian creators.</li>
                  </ul>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs text-pink-200">USDT Wallet Address *</Label>
                  <Input
                    value={form.wallet_address}
                    onChange={(e) => setForm({ ...form, wallet_address: e.target.value })}
                    placeholder="TRC20 / Polygon / BEP20 wallet address"
                    data-testid="payouts-input-wallet-address"
                    className="bg-black/40 border-pink-500/30 text-white mt-1 font-mono text-xs"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs text-pink-200">Network *</Label>
                  <select
                    value={form.network}
                    onChange={(e) => setForm({ ...form, network: e.target.value })}
                    data-testid="payouts-input-network"
                    className="w-full bg-black/40 border border-pink-500/30 rounded-md px-3 py-2 text-white mt-1 text-sm"
                  >
                    {USDT_NETWORKS.map((n) => (
                      <option key={n.id} value={n.id}>{n.label}</option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {/* Phase 9.7.17 — Bank transfer extra details */}
            {form.method === "bank" && (
              <div className="sm:col-span-2">
                <Label className="text-xs text-pink-200">Bank Details *</Label>
                <Textarea
                  value={form.bank_details}
                  onChange={(e) => setForm({ ...form, bank_details: e.target.value })}
                  placeholder={"Bank name\nAccount number\nIBAN / SWIFT / Routing\nBank country & address"}
                  rows={4}
                  data-testid="payouts-input-bank-details"
                  className="bg-black/40 border-pink-500/30 text-white mt-1 text-xs font-mono"
                />
                <p className="text-[10px] text-pink-200/60 mt-1">
                  Include everything the founder needs to wire funds.
                </p>
              </div>
            )}

            {/* Phase 9.7.17 — optional country (helps founder route the payment) */}
            <div className="sm:col-span-2">
              <Label className="text-xs text-pink-200">Country (optional)</Label>
              <Input
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                placeholder="e.g. Nigeria, United States, UK"
                data-testid="payouts-input-country"
                className="bg-black/40 border-pink-500/30 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-pink-200">Amount (USD) — min $25 *</Label>
              <Input
                type="number"
                step="0.01"
                min="25"
                max={summary?.spendable_available_usd || 0}
                value={form.amount_usd}
                onChange={(e) => setForm({ ...form, amount_usd: e.target.value })}
                placeholder="25.00"
                data-testid="payouts-input-amount"
                className="bg-black/40 border-pink-500/30 text-white mt-1"
              />
              <p className="text-[10px] text-pink-200/60 mt-1">
                Max: <button type="button"
                  className="underline hover:text-pink-100"
                  onClick={() => setForm({ ...form, amount_usd: (summary?.spendable_available_usd || 0).toString() })}>
                  {fmtUSD(summary?.spendable_available_usd)}
                </button>
              </p>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-pink-200">Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Anything we should know? (optional)"
                rows={2}
                data-testid="payouts-input-notes"
                className="bg-black/40 border-pink-500/30 text-white mt-1 text-sm"
              />
            </div>
            <div className="sm:col-span-2">
              <Button
                type="submit"
                disabled={submitting || (summary?.spendable_available_usd || 0) < 25}
                className="w-full bg-gradient-to-r from-emerald-500 to-pink-500 hover:from-emerald-400 hover:to-pink-400 text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="payouts-submit-btn"
              >
                {submitting ? "Submitting…"
                  : (summary?.spendable_available_usd || 0) < 25
                  ? "Earn at least $25 to cash out"
                  : "💰 Submit Payout Request"}
              </Button>
            </div>
          </form>
        </Card>

        {/* History */}
        <Card className="p-5 bg-black/40 border-pink-500/20" data-testid="payouts-history-card">
          <h2 className="text-lg font-bold text-white mb-3">Payout History</h2>
          {history.length === 0 ? (
            <p className="text-pink-200/60 text-sm" data-testid="payouts-history-empty">
              No payout requests yet.
            </p>
          ) : (
            <div className="space-y-2">
              {history.map((p) => {
                const sb = STATUS_BADGE[p.status] || STATUS_BADGE.pending;
                return (
                  <div
                    key={p.request_id}
                    className="flex items-center gap-3 p-3 bg-black/30 border border-pink-500/15 rounded-lg flex-wrap"
                    data-testid={`payouts-history-row-${p.request_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-white">
                        {fmtUSD(p.amount_usd)} via {p.method}
                        {p.network && (
                          <span className="ml-1 text-[10px] text-amber-300 uppercase">
                            · {p.network}
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-pink-200/60 truncate">
                        {p.wallet_address
                          ? <span className="font-mono">{p.wallet_address}</span>
                          : p.handle}
                        {" "}· requested {(p.created_at || "").slice(0, 10)}
                        {p.paid_at && <> · paid {p.paid_at.slice(0, 10)}</>}
                        {p.reference_id && <> · ref: {p.reference_id}</>}
                      </p>
                      {p.admin_notes && (
                        <p className="text-[11px] text-pink-200/70 italic mt-1">
                          Admin note: {p.admin_notes}
                        </p>
                      )}
                    </div>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${sb.cls}`}>
                      {sb.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default PayoutsPage;
